import { Employee, BasicCompany } from 'src/types';
import { intercomId } from './env';

// Intercom is switched off in every environment for now. The integration stays
// so it can be turned back on here; with it off the widget never loads, so
// every other Intercom call no-ops and the Help page is hidden.
export const isIntercomEnabled = false;

/** The launcher's default gap from the bottom of the window, in px. */
export const INTERCOM_VERTICAL_PADDING = 24;

export const initializeIntercom = () => {
  if (!isIntercomEnabled) return;

  const APP_ID = 'xl2ojsh9';

  (function () {
    const w = window;
    const ic = w.Intercom;
    if (typeof ic === 'function') {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
    } else {
      const d = document;
      const i = function (...params: unknown[]) {
        i.c(params);
      };
      i.q = [] as unknown[];
      i.c = function (args: unknown) {
        i.q.push(args);
      };
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      w.Intercom = i;
      const l = function () {
        const s = d.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = `https://widget.intercom.io/widget/${APP_ID}`;
        const x = d.getElementsByTagName('script')[0];
        x.parentNode?.insertBefore(s, x);
      };
      if (document.readyState === 'complete') {
        l();
      } else {
        w.addEventListener('load', l, false);
      }
    }
  })();

  window.intercomSettings = {
    app_id: APP_ID,
    alignment: 'right',
    action_color: '#04264b',
    background_color: '#04264b',
    horizontal_padding: 24,
    vertical_padding: INTERCOM_VERTICAL_PADDING,
    /*
    we need to specify the className for the launcher element
    to have the ability to close/open the chat in different places
    */
    custom_launcher_selector: '.intercom-help-launcher',
  };

  const intercom = window.Intercom;
  if (typeof intercom === 'function') {
    intercom('boot', {
      app_id: intercomId,
    });
  }
};

export const updateIntercomUser = (authUser: Employee, userCompanies: BasicCompany[] = []) => {
  if (!isIntercomEnabled || !window.Intercom) return;

  const userData: Intercom_.IntercomSettings = {
    user_id: authUser.key,
    user_hash: authUser.intercomHash ?? undefined,
    email: authUser.email ?? undefined,
    name: `${authUser.givenName} ${authUser.familyName}`,
    role: capitalizeFirstLetter(authUser.role),
  };

  const getCompanyData = (c: BasicCompany) => {
    const companyData: Intercom_.IntercomCompany = {
      company_id: c.key,
      name: c.name,
    };
    if (c.vanLine) companyData.van_line = c.vanLine;
    if (c.parentCompanyKey) companyData.parent_company_key = c.parentCompanyKey;

    return companyData;
  };

  if (userCompanies.length === 1) {
    const [company] = userCompanies;
    userData.company = getCompanyData(company);
    if (company.parentCompanyKey) userData.parent_company_key = company.parentCompanyKey;
  } else if (userCompanies.length > 1) {
    const parentCompanyKeys = new Set();
    userData.companies = [];

    for (const company of userCompanies) {
      if (company.parentCompanyKey) parentCompanyKeys.add(company.parentCompanyKey);
      userData.companies.push(getCompanyData(company));
    }
    if (parentCompanyKeys.size) userData.parent_company_keys = [...parentCompanyKeys].join(', ');
  }

  window.Intercom('update', userData);
};

export const hideIntercomIfDisabled = (userCompanies: BasicCompany[] = []) => {
  // If any of the auth user's companies require support to be disabled, then disable it
  const isSupportDisabled = userCompanies.some((c) => !c.isSupportEnabled);
  if (!isSupportDisabled) return;

  const intercom = window.Intercom;
  if (typeof intercom === 'function') {
    intercom('update', {
      hide_default_launcher: true,
    });

    // Prevent intercom from loading again
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.Intercom = null;
  }
};

/**
 * Move the launcher's distance from the bottom of the window, to lift it clear
 * of something pinned there. Intercom is null once support is disabled.
 */
export const setIntercomVerticalPadding = (verticalPadding: number) => {
  if (!isIntercomEnabled || typeof window.Intercom !== 'function') return;

  window.Intercom('update', { vertical_padding: verticalPadding });
};

function capitalizeFirstLetter(letters: string) {
  return letters.charAt(0).toUpperCase() + letters.slice(1);
}
